import { LogicEngine } from '../LogicEngine/LogicEngine';

// Library for Events that are strongly typed
// see https://github.com/vitaly-t/sub-events
import { SubEvent } from 'sub-events';
import { PageStepType } from '../PageParser/PageTypes';
import { DataServices } from '../utils/DataServices';
import { ParticipantSendMessageResult } from '../types/ParticipantSendMessageResult';

// TODO:  Move these to configuration items that come from the server
// in the login result.

const strLainaInstanceKey = "9cd21a12-80bd-4643-8c18-b353ef56fa0c";
const strLainaPrimaryKey = "515AD85DF47AD49F61B4FE821CCD82F526D9154250C98623F877293EC0A96858";

/**
 *

 Notification Manager sends events when certain things happen.  These can be subscribed through the PageEngine
 that hosts the notification manager.

 Reference implementation:
 https://gitlab.protovate.com/lainaedge/lainaedcportal/-/blob/master/src/components/MainPageRender.tsx#L115

 evInitializeClient - Fired when initialized

 See reference implementation for
 page.notificationManager.evSubscribe.subscribe((data: NotificationSubscribeEvent) => {

 evSubscribe - - Fired when the client should subscribe to a pusher beams notification instance.  The
 instance ID is sent as a parameter (see NotificationSubscribeEvent).

 Note that you must set the device ID.   See the reference implementation on page.notificationManger.setDeviceID

 */

export interface NotificationInitializeEvent
{
	instanceKey: string;
	notification: NotificationManager;
};

export interface NotificationSubscribeEvent
{
	topic: string;
	instanceKey: string;
	notification: NotificationManager;
};

export class NotificationManager
{
	logicRef: LogicEngine | undefined;

	//  Holder for the evice ID once registered
	device_id: string | undefined;

	//  Holder for the list of subscribed topics, should be updated by client
	my_topics: string[] = [];

	//  Initialization Request, the instance key is passed to the event.
	evInitializeClient: SubEvent<NotificationInitializeEvent> = new SubEvent();

	//  Request the client subscribe to a topic
	evSubscribe: SubEvent<NotificationSubscribeEvent> = new SubEvent();

	hasTopic(topicName: string)
	{
		for (let name of this.my_topics)
		{
			if (name.toLocaleLowerCase().trim() === name.toLocaleLowerCase().trim())
				return true;
		}

		return false;
	}

	/** Return the instance key */
	getInstanceKey(): string
	{
		return strLainaInstanceKey;
	}

	/** The render should call this one the device is registered */
	setDeviceID(strDeviceID: string)
	{
		console.log("NotificationManager: Initialize Client with Beams:", strDeviceID);
		this.device_id = strDeviceID;
		if (this.logicRef)
			this.logicRef.assign("beams_device", this.device_id);
	}

	/** Get a list of topics that we are subscribed to, update the logic engine with the name of the topic
	as beams_<topic> = true, and send out an event to any listeners.
	*/
	setMyTopics(list: string[])
	{
		//  Get the current registrations
		this.my_topics = list;
		console.log("NotificationManager: Current subscribed interests:", this.my_topics);
		for (let name of this.my_topics)
		{
			if (this.logicRef)
				this.logicRef.assign("beams_" + name, true);
		}
	}

	/** Connect connect:
	*  Step Options: Connect
	*  Connect to the beams service, get the device id and topics.
	*/
	async processConnect(step: PageStepType)
	{
		this.evInitializeClient.emit({ instanceKey: this.getInstanceKey(), notification: this });
	}

	/**
	*  Step Options: Register
	*  Text: Name of interest
	*/
	async processRegister(step: PageStepType)
	{
		let topic = step.text_en;
		if (!topic || topic === "")
		{
			console.log("NotificationManager: Unable to register because text is null")
			return false;
		}

		if (this.logicRef)
		{
			topic = this.logicRef.processTextReplacement(topic).replace(/ /g, "");
		} else
		{
			console.log("Missing logic reference")
		}

		if (!this.hasTopic(topic))
			this.evSubscribe.emit({ topic: topic, instanceKey: this.getInstanceKey(), notification: this })
	}

	/* Send a message to a participant */
	async sendNotificaitonToParticipant(pid: string, messageText: string, messageBody: string, reason: string): Promise<ParticipantSendMessageResult>
	{
		let result = await DataServices.instance().sendMessage(pid, messageText, messageBody, reason);
		console.log("Notification Manager Result for pid=", pid, " messageText=", messageText, "body=", messageBody, "reason=", reason, "result=", result);
		return result;
	}

	/* Send an email message to a participant */
	async sendEmailToParticipant(pid: string, messageText: string, messageBody: string, reason: string): Promise<ParticipantSendMessageResult>
	{
		let result = await DataServices.instance().sendEmail(pid, messageText, messageBody, reason);
		console.log("Notification Manager Result for pid=", pid, "result=", result);
		return result;
	}

	/* Send an email message to a participant */
	async sendEmailToSite(site_id: string, messageText: string, messageBody: string, reason: string): Promise<ParticipantSendMessageResult>
	{
		let result = await DataServices.instance().sendSiteEmail(site_id, messageText, messageBody, reason);
		console.log("Notification Manager Result for pid=", site_id, "result=", result);
		return result;
	}

	async sendSmsToParticipant(pid: string, messageText: string, phoneNumber: string, reason: string): Promise<ParticipantSendMessageResult>
	{
		let result = await DataServices.instance().sendSms(pid, messageText, phoneNumber, reason);
		console.log("Notification Manager Result for pid=", pid, "result=", result);
		return result;
	}

}

